import type * as React from 'react';

import { Button, type ButtonProps } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { Modal } from './Modal';

export type ConfirmModalProps = {
    kind?: ButtonProps['kind'];
    title: string;
    children: React.ReactNode;
    confirmButtonText?: string;
    onComplete: (confirmed: boolean) => void | Promise<void>;
    testID?: string;
};

/**
 * Modal to ask the user to confirm an action. If a promise is passed as an onComplete handler,
 * the modal will wait for the promise to complete before closing.
 */
export function ConfirmModal(props: ConfirmModalProps): React.ReactElement {
    const {
        kind = 'primary',
        title,
        children,
        confirmButtonText = 'Confirm',
        onComplete,
        testID,
    } = props;
    const [canceling, wrapCanceling, setCanceling] = useLoadingState();
    const [confirming, wrapConfirming, setConfirming] = useLoadingState();

    const onConfirm = async () => {
        setConfirming(true);
        await onComplete(true);
    };

    const onCancel = async () => {
        setCanceling(true);
        await onComplete(false);
    };

    return (
        <Modal
            title={title}
            onClose={onCancel}
            testID={testID}
            footer={
                <div className="flex justify-end gap-2">
                    <Button
                        kind="secondary"
                        onPress={onCancel}
                        loadable={[canceling, wrapCanceling]}
                        disabled={confirming}
                        label="Cancel"
                    />
                    <Button
                        label={confirmButtonText}
                        kind={kind}
                        loadable={[confirming, wrapConfirming]}
                        disabled={canceling}
                        onPress={onConfirm}
                    />
                </div>
            }
        >
            <div>{children}</div>
        </Modal>
    );
}
